import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, TrendingUp, Activity, DollarSign, Signal, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get('/admin/dashboard');
      setMetrics(response.data.metrics);
      setSystemStatus(response.data.system_status);
    } catch (error) {
      toast.error('Dashboard yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-400">Yükleniyor...</div>;
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">🎛️ Admin Dashboard</h1>
        <p className="text-slate-400">System overview and key metrics</p>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="border-green-500/30 bg-gradient-to-br from-green-900/20 to-transparent">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-400" />
              <div>
                <div className="text-sm text-slate-400">Platform Status</div>
                <div className="text-xl font-bold text-green-400">{systemStatus?.platform_status || 'Running'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-500/30 bg-gradient-to-br from-blue-900/20 to-transparent">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Activity className="h-8 w-8 text-blue-400" />
              <div>
                <div className="text-sm text-slate-400">Data Feed</div>
                <div className="text-xl font-bold text-blue-400">{systemStatus?.data_feed_status || 'Operational'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-transparent">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Signal className="h-8 w-8 text-purple-400" />
              <div>
                <div className="text-sm text-slate-400">Last Signal</div>
                <div className="text-sm font-bold text-purple-400">
                  {systemStatus?.last_signal_timestamp ? new Date(systemStatus.last_signal_timestamp).toLocaleTimeString('tr-TR') : 'N/A'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Metrics */}
      <h2 className="text-2xl font-bold mb-4">📊 Key Metrics</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-400" />
              Total Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold font-mono">{metrics?.total_users || 0}</div>
            <div className="text-sm text-slate-400 mt-2">
              {metrics?.active_users_24h || 0} active last 24h
            </div>
            <div className="text-xs text-green-400 mt-1 font-semibold">
              +5 today
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-400" />
              Strategies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold font-mono">{metrics?.total_strategies || 0}</div>
            <div className="text-sm text-slate-400 mt-2">
              {metrics?.active_strategies || 0} active
            </div>
            <div className="text-xs text-green-400 mt-1 font-semibold">
              +2 this week
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Signal className="h-5 w-5 text-purple-400" />
              Signals Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold font-mono">{metrics?.signals_today || 0}</div>
            <div className="text-sm text-slate-400 mt-2">Generated today</div>
            <div className="text-xs text-red-400 mt-1 font-semibold">
              -12% vs yesterday
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-yellow-400" />
              Monthly Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold font-mono text-yellow-400">${metrics?.monthly_revenue || 0}</div>
            <div className="text-sm text-slate-400 mt-2">Current month</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
